import { useState, useRef, useEffect } from 'react'
import {
  Settings,
  Plus,
  LogOut,
  Menu,
  Mail,
  User,
  ChevronUp,
  HelpCircle,
  Shield,
  Scale,
  Accessibility
} from 'lucide-react'
import { useAuth } from '../contexts/AuthContext'
import { useLocale } from '../contexts/LocaleContext'
import { useSubscription } from '../contexts/SubscriptionContext'
import {
  ProposalNewIcon,
  TemplateIcon,
  ClientsIcon,
  IntegrationsIcon,
  BillingIcon
} from './icons/CoreIcons'
import api from '../lib/api'

interface AppLayoutProps {
  children: React.ReactNode
  currentView: 'dashboard' | 'editor' | 'templates' | 'settings' | 'profile' | 'clients' | 'client-profile' | 'billing' | 'admin' | 'email' | 'integrations'
  onNavigate: (view: 'dashboard' | 'editor' | 'templates' | 'settings' | 'profile' | 'clients' | 'client-profile' | 'billing' | 'admin' | 'email' | 'integrations') => void
  onNewProposal: () => void
}

export default function AppLayout({ children, currentView, onNavigate, onNewProposal }: AppLayoutProps) {
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [showProfileMenu, setShowProfileMenu] = useState(false)
  const profileMenuRef = useRef<HTMLDivElement>(null)
  const { user, signOut } = useAuth()
  const { tx, isRTL } = useLocale()
  const { isPro, isBusiness } = useSubscription()
  const [isAdmin, setIsAdmin] = useState(false)
  const planLabel = isBusiness ? 'Business' : isPro ? 'Pro' : 'Free'

  useEffect(() => {
    api.checkAdmin().then(res => setIsAdmin(res.is_admin)).catch(() => setIsAdmin(false))
  }, [])

  // Close profile menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (profileMenuRef.current && !profileMenuRef.current.contains(event.target as Node)) {
        setShowProfileMenu(false)
      }
    }
    if (showProfileMenu) {
      document.addEventListener('mousedown', handleClickOutside)
      return () => document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [showProfileMenu])

  const mainNavItems: { id: string; label: string; icon: React.ComponentType<any> }[] = [
    { id: 'dashboard', label: tx('My Work', 'העבודות שלי'), icon: ProposalNewIcon },
    { id: 'clients', label: tx('Contacts', 'אנשי קשר'), icon: ClientsIcon },
    { id: 'templates', label: tx('Templates', 'תבניות'), icon: TemplateIcon },
    { id: 'integrations', label: tx('Connect', 'חיבורים'), icon: IntegrationsIcon },
    { id: 'email', label: tx('Inbox', 'דואר נכנס'), icon: Mail },
    { id: 'settings', label: tx('Settings', 'הגדרות'), icon: Settings },
    ...(isAdmin ? [{ id: 'admin', label: tx('Admin', 'ניהול'), icon: Shield }] : []),
  ]


  return (
    <div className="flex h-screen-safe bg-slate-50" dir={isRTL ? 'rtl' : 'ltr'}>
      {/* Mobile Sidebar Overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar - Light theme like the reference */}
      <aside
        className={`fixed lg:static inset-y-0 ${isRTL ? 'right-0 border-l' : 'left-0 border-r'} w-20 bg-white border-slate-200 z-50 transform transition-transform duration-200 lg:translate-x-0 ${
          sidebarOpen ? 'translate-x-0' : isRTL ? 'translate-x-full lg:translate-x-0' : '-translate-x-full lg:translate-x-0'
        } flex flex-col`}
      >
        {/* Logo */}
        <div className="p-4 flex flex-col items-center">
          <img src="/media/quor_icon.png" alt="QUOR - Digital Signatures Made Simple" className="w-10 h-10 rounded-lg" />
        </div>

        {/* Main Navigation */}
        <nav className="flex-1 flex flex-col items-center py-4 space-y-1">
          {mainNavItems.map((item) => {
            const Icon = item.icon
            const isActive = currentView === item.id
            const isClickable = item.id === 'dashboard' || item.id === 'templates' || item.id === 'settings' || item.id === 'clients' || item.id === 'admin' || item.id === 'email' || item.id === 'integrations'

            return (
              <button
                key={item.id}
                onClick={() => {
                  if (isClickable) {
                    onNavigate(item.id as 'dashboard' | 'templates' | 'settings' | 'clients' | 'billing' | 'admin' | 'email' | 'integrations')
                  }
                  setSidebarOpen(false)
                }}
                className={`w-16 flex flex-col items-center gap-1 py-3 px-2 rounded-xl text-xs font-medium transition-all ${
                  isActive
                    ? 'bg-slate-50 text-slate-700'
                    : 'text-slate-500 hover:bg-slate-50 hover:text-slate-700'
                } ${!isClickable ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                <Icon className="w-6 h-6" strokeWidth={1.5} />
                <span>{item.label}</span>
              </button>
            )
          })}
        </nav>

        {/* Bottom Section */}
        <div className="flex flex-col items-center py-4 space-y-1 border-t border-slate-100">
          {/* Plan / Upgrade Button */}
          <button
            onClick={() => {
              onNavigate('billing')
              setSidebarOpen(false)
            }}
            className={`w-16 flex flex-col items-center gap-1.5 py-2 px-2 rounded-xl text-xs transition-all ${
              currentView === 'billing'
                ? 'bg-slate-50'
                : 'hover:bg-slate-50'
            }`}
          >
            {/* Premium Text Badge */}
            <div className={`px-3 py-1.5 rounded-lg font-semibold text-[11px] tracking-wide ${
              isBusiness
                ? 'bg-gradient-to-r from-amber-100 to-yellow-100 text-amber-700 border border-amber-200'
                : isPro
                  ? 'bg-gradient-to-r from-violet-100 to-purple-100 text-violet-700 border border-violet-200'
                  : 'bg-slate-100 text-slate-500 border border-slate-200'
            }`}>
              {planLabel}
            </div>
            <span className={`text-[10px] ${
              isBusiness || isPro ? 'text-slate-400' : 'text-rose-400'
            }`}>
              {isBusiness || isPro ? tx('Plan', 'תוכנית') : tx('Upgrade', 'שדרג')}
            </span>
          </button>

          {/* User Avatar with Dropdown */}
          <div className="pt-2 relative" ref={profileMenuRef}>
            <button
              onClick={() => setShowProfileMenu(!showProfileMenu)}
              className="w-10 h-10 rounded-full bg-accent-500 flex items-center justify-center text-white font-medium hover:bg-accent-600 transition-all shadow-lg shadow-slate-500/25 overflow-hidden"
            >
              {user?.avatarUrl ? (
                <img src={user.avatarUrl} alt="" className="w-full h-full object-cover" />
              ) : (
                user?.email?.[0]?.toUpperCase() || 'U'
              )}
            </button>

            {/* Profile Dropdown Menu */}
            {showProfileMenu && (
              <div className={`absolute bottom-14 ${isRTL ? 'right-0' : 'left-0'} w-64 bg-white rounded-xl shadow-2xl border border-slate-200 overflow-hidden z-50 animate-in fade-in slide-in-from-bottom-2 duration-200`}>
                {/* User Info Header - gradient handled by global overrides */}
                <div className={`p-4 ${isRTL ? 'bg-gradient-to-l' : 'bg-gradient-to-r'} bg-accent-50 border-b border-slate-100`}>
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-full bg-accent-500 flex items-center justify-center text-white font-semibold text-lg shadow-lg overflow-hidden">
                      {user?.avatarUrl ? (
                        <img src={user.avatarUrl} alt="" className="w-full h-full object-cover" />
                      ) : (
                        user?.email?.[0]?.toUpperCase() || 'U'
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-slate-900 truncate">
                        {user?.email?.split('@')[0] || tx('User', 'משתמש')}
                      </p>
                      <p className="text-xs text-slate-500 truncate">
                        {user?.email || 'user@example.com'}
                      </p>
                    </div>
                    <ChevronUp className="w-4 h-4 text-slate-400" />
                  </div>
                </div>

                {/* Menu Items */}
                <div className="py-2">
                  <button
                    onClick={() => {
                      setShowProfileMenu(false)
                      onNavigate('profile')
                    }}
                    className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-slate-700 hover:bg-slate-50 transition-colors"
                  >
                    <User className="w-4 h-4 text-slate-400" />
                    <span>{tx('My Profile', 'הפרופיל שלי')}</span>
                  </button>
                  <button
                    onClick={() => {
                      setShowProfileMenu(false)
                      onNavigate('settings')
                    }}
                    className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-slate-700 hover:bg-slate-50 transition-colors"
                  >
                    <Settings className="w-4 h-4 text-slate-400" />
                    <span>{tx('Settings', 'הגדרות')}</span>
                  </button>
                  <button
                    onClick={() => {
                      setShowProfileMenu(false)
                      onNavigate('billing')
                    }}
                    className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-slate-700 hover:bg-slate-50 transition-colors"
                  >
                    <BillingIcon className="w-4 h-4 text-slate-400" />
                    <span>{tx('Billing & Payments', 'חיוב ותשלומים')}</span>
                  </button>
                  <button
                    className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-slate-700 hover:bg-slate-50 transition-colors"
                  >
                    <HelpCircle className="w-4 h-4 text-slate-400" />
                    <span>{tx('Help & Support', 'עזרה ותמיכה')}</span>
                  </button>
                </div>

                {/* Legal Links */}
                <div className="border-t border-slate-100 py-2">
                  <a
                    href="/privacy"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-full flex items-center gap-3 px-4 py-2 text-sm text-slate-600 hover:bg-slate-50 transition-colors"
                  >
                    <Shield className="w-4 h-4 text-slate-400" />
                    <span>{tx('Privacy Policy', 'מדיניות פרטיות')}</span>
                  </a>
                  <a
                    href="/terms"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-full flex items-center gap-3 px-4 py-2 text-sm text-slate-600 hover:bg-slate-50 transition-colors"
                  >
                    <Scale className="w-4 h-4 text-slate-400" />
                    <span>{tx('Terms of Service', 'תנאי שימוש')}</span>
                  </a>
                  <a
                    href="/accessibility"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-full flex items-center gap-3 px-4 py-2 text-sm text-slate-600 hover:bg-slate-50 transition-colors"
                  >
                    <Accessibility className="w-4 h-4 text-slate-400" />
                    <span>{tx('Accessibility', 'נגישות')}</span>
                  </a>
                </div>

                {/* Logout */}
                <div className="border-t border-slate-100 py-2">
                  <button
                    onClick={() => {
                      setShowProfileMenu(false)
                      signOut()
                    }}
                    className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-rose-600 hover:bg-rose-50 transition-colors"
                  >
                    <LogOut className="w-4 h-4" />
                    <span>{tx('Sign Out', 'התנתקות')}</span>
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Mobile Header */}
        <header className="lg:hidden bg-white border-b border-slate-200 px-4 py-3 mobile-header-safe flex items-center justify-between sticky top-0 z-30">
          <button
            onClick={() => setSidebarOpen(true)}
            className="p-2 hover:bg-slate-100 rounded-lg"
          >
            <Menu className="w-5 h-5 text-slate-600" />
          </button>
          <div className="flex items-center gap-2">
            <img src="/media/quor_icon.png" alt="QUOR - Digital Signatures Made Simple" className="w-8 h-8 rounded-lg" />
            <span className="font-semibold text-slate-800">Quor</span>
          </div>
          <button
            onClick={onNewProposal}
            className="p-2 bg-slate-500 text-white rounded-lg hover:bg-slate-700"
          >
            <Plus className="w-5 h-5" />
          </button>
        </header>

        {/* Page Content */}
        <main className="flex-1 overflow-auto">
          {children}
        </main>
      </div>
    </div>
  )
}
